// ============================================================================
// Unsaved project edits kept on this device (outage lane, 2026-09-29)
// ----------------------------------------------------------------------------
// Project writes are API-only: a save the server cannot take writes nothing
// local (data-service.js, the v5.12.0 contract), so an edit made while
// QuoteMaker's server was not answering lived only in the tab's memory. On
// 2026-09-29 Customer Zero refreshed during a backend deploy and lost it.
//
// This keeps the exact envelope the save could not send, in this browser's
// tenant database (Dexie `app_settings`, one row per project), with the
// version token it descends from. It survives a reload and signing in again
// as the same person. When the project next opens, the load paints it back
// and marks the project unsaved; the normal save then sends it with that
// token, so the server refuses it if the row moved (the conflict flow), and
// a successful save clears the row.
//
// This is not an outbox: nothing here sends anything. It only makes sure the
// operator's own edits are still on screen when the server answers.
//
// A project that was never saved has no id yet (the review of this lane found
// its edits kept nowhere, so a refresh during the outage lost them). It is kept
// in NEW_PROJECT_SLOT: one row per person on this device. The QuoteMaker page
// paints it back when it opens on a blank new project, and its first
// successful save clears it.
// ============================================================================

import db from './db';
import { readIdentityFromAccessToken } from './local-tenant-database';

const KEY_PREFIX = 'qm-unsaved-project-edits:';

/**
 * Stands in for the id of a project that has never been saved. Never a real
 * project id (those are UUIDs, or legacy `proj_` ids).
 */
export const NEW_PROJECT_SLOT = 'new-project';

export interface KeptProjectEdits {
    version: 1;
    projectId: string;
    /** The person who made the edits. Another person on this device never gets them. */
    userId: string | null;
    /** The version token the edits descend from (what the save presented). */
    expectedUpdatedAt: string | null;
    /** The server content signature the edits were based on, for the stale-write self-heal. */
    baselineSignature: string | null;
    /** The editor state these edits came from, so the autosave keeps each state once. */
    fingerprint: string | null;
    keptAt: string;
    title: string | null;
    envelope: Record<string, unknown>;
}

export interface KeepUnsavedProjectEditsInput {
    projectId: string;
    expectedUpdatedAt: string | null;
    baselineSignature?: string | null;
    fingerprint?: string | null;
    envelope: Record<string, unknown>;
    /**
     * Write the copy to this device's database (default true). A Quote Run
     * passes false: it drives the server's copy and never restores, so only
     * the state is remembered, which stops the autosave repeating it.
     */
    onDevice?: boolean;
    /**
     * Record that the open editor holds these edits (default true). False for
     * a save the tab has since moved on from: the copy is still written, but
     * the banner and the next save of a different project must not claim it.
     */
    trackInEditor?: boolean;
}

// Which editor state was last handled while the server was not answering,
// per project. The autosave reads it so an unchanged state is kept once, not
// on every effect run. Set even when the local write fails, for the same
// reason: a failing local store must not become a retry loop.
const _keptFingerprints = new Map<string, string>();
// Projects whose unsent edits are actually in the local database now, so the
// status banner never says "kept on this device" about edits that are not.
const _keptOnDevice = new Set<string>();

// Keys a kept envelope never paints over the server row: identity, the
// version token, and columns the server owns (lifecycle, CRM links, numbers).
const SERVER_OWNED_KEYS = new Set([
    'id', 'dexieId', 'expectedUpdatedAt', 'mediaRetirementReceipts',
    'updated_at', 'updatedAt', 'created_at', 'createdAt',
    'quoteStatus', 'quoteStatusChangedAt', 'quote_status', 'quote_status_changed_at',
    'projectType', 'project_type', 'status',
    'company_id', 'property_id', 'companyId', 'propertyId',
    'qmQuoteNo', 'qm_quote_no',
    'emailSent', 'emailSentAt', 'pdfGenerated', 'pdfGeneratedAt',
]);

function keyFor(projectId: string) {
    // One new-project row per person, so a second person on this device can
    // neither read nor overwrite it.
    if (projectId === NEW_PROJECT_SLOT) return `${KEY_PREFIX}${NEW_PROJECT_SLOT}:${currentUserId() || 'unknown-user'}`;
    return `${KEY_PREFIX}${projectId}`;
}

function currentUserId(): string | null {
    try {
        const token = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('qm_access_token') : null;
        return readIdentityFromAccessToken(token)?.userId || null;
    } catch {
        return null;
    }
}

function jsonSafe<T>(value: T): T {
    return JSON.parse(JSON.stringify(value)) as T;
}

/** Keep the envelope a save could not send. Never throws; false when nothing was stored. */
export async function keepUnsavedProjectEdits(input: KeepUnsavedProjectEditsInput): Promise<boolean> {
    const projectId = input?.projectId ? String(input.projectId) : '';
    if (!projectId || !input.envelope || typeof input.envelope !== 'object') return false;
    const trackInEditor = input.trackInEditor !== false;
    if (input.fingerprint && trackInEditor) _keptFingerprints.set(projectId, input.fingerprint);
    if (input.onDevice === false) return false;
    try {
        const envelope = jsonSafe(input.envelope);
        const record: KeptProjectEdits = {
            version: 1,
            projectId,
            userId: currentUserId(),
            expectedUpdatedAt: input.expectedUpdatedAt || null,
            baselineSignature: input.baselineSignature || null,
            fingerprint: input.fingerprint || null,
            keptAt: new Date().toISOString(),
            title: typeof envelope.title === 'string' ? envelope.title : null,
            envelope,
        };
        await (db as any).app_settings.put({ key: keyFor(projectId), value: record });
        if (trackInEditor) _keptOnDevice.add(projectId);
        return true;
    } catch (err: any) {
        console.warn('[QM] Could not keep unsaved project edits on this device:', err?.message || err);
        return false;
    }
}

/** The kept edits for a project, for the person signed in now, or null. */
export async function readUnsavedProjectEdits(projectId: unknown): Promise<KeptProjectEdits | null> {
    if (!projectId) return null;
    try {
        const row = await (db as any).app_settings.get(keyFor(String(projectId)));
        const record = row?.value as KeptProjectEdits | undefined;
        if (!record || record.version !== 1 || record.projectId !== String(projectId)) return null;
        if (!record.envelope || typeof record.envelope !== 'object') return null;
        const userId = currentUserId();
        if (record.userId && userId && record.userId !== userId) return null;
        return record;
    } catch {
        return null;
    }
}

/** The edits were saved (or deliberately discarded): forget them. Never throws. */
export async function clearUnsavedProjectEdits(projectId: unknown): Promise<void> {
    if (!projectId) return;
    _keptFingerprints.delete(String(projectId));
    _keptOnDevice.delete(String(projectId));
    try {
        await (db as any).app_settings.delete(keyFor(String(projectId)));
    } catch { /* nothing kept, or the store is closed: nothing to clear */ }
}

/**
 * The editor has left the new project (a reset, or another project opened).
 * This tab no longer holds its edits, so the banner and the next new project's
 * save must not treat them as its own. The copy stays on the device: the next
 * blank new project paints it back.
 */
export function releaseNewProjectSlot(): void {
    _keptFingerprints.delete(NEW_PROJECT_SLOT);
    _keptOnDevice.delete(NEW_PROJECT_SLOT);
}

export function getKeptUnsavedEditsFingerprint(projectId: unknown): string | null {
    if (!projectId) return null;
    return _keptFingerprints.get(String(projectId)) ?? null;
}

const RESTORED_MARKER = 'restored-from-this-device';

/** A load painted kept edits back: the next successful save says they were saved. */
export function noteRestoredUnsavedEdits(projectId: unknown): void {
    if (!projectId) return;
    if (!_keptFingerprints.has(String(projectId))) _keptFingerprints.set(String(projectId), RESTORED_MARKER);
    // Painted back from the local database, so they are there.
    _keptOnDevice.add(String(projectId));
}

/** True when this project's unsent edits are in the local database right now. */
export function isKeptOnDevice(projectId: unknown): boolean {
    if (!projectId) return false;
    return _keptOnDevice.has(String(projectId));
}

/**
 * What the status banner may claim: the open project's latest edit is in the
 * local database. `latestEditFingerprint` is the editor state of the last edit
 * made during this outage (null when none was made yet). A later edit that has
 * not been kept yet makes this false, so the banner never says "kept on this
 * device" about edits that are only on screen (outage lane review, F1). A
 * project with no id asks about the new-project slot.
 */
export function isLatestEditKeptOnDevice(projectId: unknown, latestEditFingerprint: string | null): boolean {
    const slot = projectId ? String(projectId) : NEW_PROJECT_SLOT;
    if (!_keptOnDevice.has(slot)) return false;
    if (latestEditFingerprint === null) return true;
    return _keptFingerprints.get(slot) === latestEditFingerprint;
}

/** True while this tab holds edits for the project that the server has not taken yet. */
export function hasKeptUnsavedEdits(projectId: unknown): boolean {
    if (!projectId) return false;
    return _keptFingerprints.has(String(projectId));
}

/**
 * The project to paint: the server row with the kept content on top. Server-
 * owned columns stay the server's. `versionToken` is the version the painted
 * record claims (the server's own when it has not moved, or the older one the
 * edits descend from, so the next save is refused if the row changed).
 */
export function overlayKeptProjectEdits(
    serverProject: Record<string, any>,
    kept: KeptProjectEdits,
    { versionToken }: { versionToken: string | null },
): Record<string, any> {
    const painted: Record<string, any> = { ...serverProject };
    for (const [key, value] of Object.entries(kept.envelope || {})) {
        if (SERVER_OWNED_KEYS.has(key)) continue;
        painted[key] = value;
    }
    if (versionToken) {
        painted.updatedAt = versionToken;
        painted.updated_at = versionToken;
    }
    return painted;
}
